/**
 * Вкладка «Вопросы опросника» раздела «Экология» панели администратора (ecology-survey, макет
 * панели администратора 16.09). Таблица «№ / Раздел / Вопрос / Ответ / Уточнение при «Да» / Вкл.»,
 * добавление и правка в модалке, включение галочкой (сразу сохраняется), удаление вопроса
 * администратора с подтверждением (его правила удаляются вместе с ним). Вопросы анкеты не удаляются —
 * на них опираются правила, их можно выключить.
 */
import { Fragment, useState } from 'react'
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react'
import { adminApi, type EcoQuestion, type EcoRule } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  ECO_ANSWER_TYPE_LABELS,
  ECO_CLARIFICATION_TYPE_LABELS,
  ECO_SURVEY_SECTIONS,
  ecoQuestionToInput,
} from '@/lib/ecology'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { QuestionDialog } from './question-dialog'

type DialogState = { open: false } | { open: true; question: EcoQuestion | null }

export function QuestionsTab({
  questions,
  rules,
  onQuestionsChange,
  onRulesChange,
}: {
  questions: EcoQuestion[]
  rules: EcoRule[]
  onQuestionsChange: (questions: EcoQuestion[]) => void
  onRulesChange: (rules: EcoRule[]) => void
}) {
  const [dialog, setDialog] = useState<DialogState>({ open: false })
  const [confirmDelete, setConfirmDelete] = useState<EcoQuestion | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  /** Сохранённый вопрос: порядок — как на сервере (раздел, затем порядок внутри раздела). */
  function upsert(saved: EcoQuestion) {
    const rest = questions.filter((q) => q.id !== saved.id)
    const exists = rest.length !== questions.length
    const next = exists
      ? questions.map((q) => (q.id === saved.id ? saved : q))
      : [...questions, saved]
    // Перенос в другой раздел ставит вопрос в конец раздела — как на сервере.
    const moved = exists && questions.find((q) => q.id === saved.id)?.section !== saved.section
    const ordered = moved ? [...rest, saved] : next
    onQuestionsChange([...ordered].sort((a, b) => a.section - b.section))
  }

  async function toggle(q: EcoQuestion, enabled: boolean) {
    setError(null)
    setTogglingId(q.id)
    try {
      upsert(await adminApi.ecology.updateQuestion(q.id, { ...ecoQuestionToInput(q), enabled }))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось изменить вопрос'))
    } finally {
      setTogglingId(null)
    }
  }

  async function remove() {
    if (!confirmDelete) return
    const target = confirmDelete
    setError(null)
    setDeleting(true)
    try {
      await adminApi.ecology.deleteQuestion(target.id)
      onQuestionsChange(
        questions
          .filter((q) => q.id !== target.id)
          .map((q) =>
            q.showIfQuestionId === target.id
              ? { ...q, showIfQuestionId: null, showIfAnswer: '' }
              : q,
          ),
      )
      onRulesChange(
        rules.filter((r) => r.questionId !== target.id && r.conditionQuestionId !== target.id),
      )
      setConfirmDelete(null)
    } catch (err) {
      setConfirmDelete(null)
      setError(getErrorMessage(err, 'Не удалось удалить вопрос'))
    } finally {
      setDeleting(false)
    }
  }

  const rulesOf = (id: string) =>
    rules.filter((r) => r.questionId === id || r.conditionQuestionId === id).length
  const enabledCount = questions.filter((q) => q.enabled).length

  return (
    <div>
      <Toolbar aria-label="Добавление вопроса">
        <p className="min-w-56 flex-1 text-13 text-txt-secondary">
          Вопросы клиенту в разделе «Экология». Опрос проходится по каждой площадке отдельно;
          реквизиты организации (название, ИНН, ОКВЭД, руководитель) берутся из карточки клиента и
          не спрашиваются.
        </p>
        <Button
          onClick={() => setDialog({ open: true, question: null })}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить вопрос
        </Button>
      </Toolbar>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <Panel>
        {questions.length === 0 ? (
          <EmptyState icon={ListChecks} title="Вопросов нет" description="Добавьте первый вопрос" />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>№</DataTableHeaderCell>
                  <DataTableHeaderCell>Вопрос</DataTableHeaderCell>
                  <DataTableHeaderCell>Ответ</DataTableHeaderCell>
                  <DataTableHeaderCell>Уточнение при «Да»</DataTableHeaderCell>
                  <DataTableHeaderCell className="text-center">Вкл.</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {questions.map((q, i) => {
                  const sectionStart = i === 0 || questions[i - 1].section !== q.section
                  const parent = q.showIfQuestionId
                    ? questions.find((p) => p.id === q.showIfQuestionId)
                    : undefined
                  return (
                    <Fragment key={q.id}>
                      {sectionStart ? (
                        <DataTableGroupRow colSpan={6}>
                          {ECO_SURVEY_SECTIONS[q.section - 1]}
                        </DataTableGroupRow>
                      ) : null}
                      <DataTableRow className={q.enabled ? undefined : 'opacity-60'}>
                        <DataTableCell className="text-txt-tertiary">{i + 1}</DataTableCell>
                        <DataTableCell className="min-w-72">
                          <div className="font-medium text-txt">{q.text}</div>
                          {q.hint ? (
                            <div className="mt-0.5 text-12 text-txt-tertiary">{q.hint}</div>
                          ) : null}
                          {parent ? (
                            <div className="mt-0.5 text-12 text-txt-tertiary">
                              показывается, если «{parent.text}» = {q.showIfAnswer}
                            </div>
                          ) : null}
                          {q.builtin ? (
                            <Badge variant="gray" className="mt-1">
                              из анкеты
                            </Badge>
                          ) : null}
                        </DataTableCell>
                        <DataTableCell className="text-txt-secondary">
                          {ECO_ANSWER_TYPE_LABELS[q.answerType]}
                          {q.options.length > 0 ? (
                            <div className="mt-0.5 text-12 text-txt-tertiary">
                              {q.options.join(' / ')}
                            </div>
                          ) : null}
                        </DataTableCell>
                        <DataTableCell className="min-w-48 text-txt-secondary">
                          {q.clarifications.length === 0
                            ? '—'
                            : q.clarifications.map((c) => (
                                <div key={c.key} className="mb-1 last:mb-0">
                                  <div className="text-txt">{c.label}</div>
                                  <div className="text-12 text-txt-tertiary">
                                    {ECO_CLARIFICATION_TYPE_LABELS[c.type]}
                                    {c.options.length > 0 ? `: ${c.options.join(' / ')}` : ''}
                                  </div>
                                </div>
                              ))}
                        </DataTableCell>
                        <DataTableCell className="text-center">
                          <Checkbox
                            checked={q.enabled}
                            disabled={togglingId === q.id}
                            aria-label={`Задавать вопрос «${q.text}»`}
                            onCheckedChange={(v) => void toggle(q, v === true)}
                          />
                        </DataTableCell>
                        <DataTableCell className="text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="secondary"
                              title="Изменить"
                              aria-label={`Изменить вопрос «${q.text}»`}
                              onClick={() => setDialog({ open: true, question: q })}
                            >
                              <Pencil className="size-4 stroke-current" aria-hidden />
                            </Button>
                            {q.builtin ? null : (
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Удалить"
                                aria-label={`Удалить вопрос «${q.text}»`}
                                onClick={() => setConfirmDelete(q)}
                              >
                                <Trash2 className="size-4 stroke-current" aria-hidden />
                              </Button>
                            )}
                          </div>
                        </DataTableCell>
                      </DataTableRow>
                    </Fragment>
                  )
                })}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </Panel>
      <p className="mt-2 text-12 text-txt-tertiary">
        Вопросов: {questions.length} · включено: {enabledCount}
      </p>

      <QuestionDialog
        open={dialog.open}
        question={dialog.open ? dialog.question : null}
        questions={questions}
        onClose={() => setDialog({ open: false })}
        onSaved={(saved) => {
          upsert(saved)
          setDialog({ open: false })
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Удаление вопроса"
        message={
          confirmDelete
            ? rulesOf(confirmDelete.id) > 0
              ? `Удалить вопрос «${confirmDelete.text}»? Вместе с ним удалятся правила, которые на него ссылаются (${rulesOf(confirmDelete.id)}).`
              : `Удалить вопрос «${confirmDelete.text}»?`
            : ''
        }
        onClose={() => setConfirmDelete(null)}
        closeDisabled={deleting}
        buttons={[
          {
            label: deleting ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleting,
            onClick: () => void remove(),
          },
          {
            label: 'Отмена',
            variant: 'secondary',
            disabled: deleting,
            onClick: () => setConfirmDelete(null),
          },
        ]}
      />
    </div>
  )
}
